import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { ClockHourService } from '@web-frontend/shared/services';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { ShiftHistory } from './shift-history.types';
import { Subscription } from 'rxjs';

@Component({
  selector: 'roma-shift-history',
  templateUrl: './shift-history.component.html',
  styleUrls: ['./shift-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slideDown', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-10px)' }),
        animate(
          '200ms ease-out',
          style({ opacity: 1, transform: 'translateY(0)' })
        ),
      ]),
      transition(':leave', [
        animate(
          '150ms ease-in',
          style({ opacity: 0, transform: 'translateY(-10px)' })
        ),
      ]),
    ]),
  ],
})
export class ShiftHistoryComponent implements OnInit, OnDestroy {
  @Input() id: string;

  changes: ShiftHistory.FormattedChange[] = [];
  isOpen = false;
  isLoading = false;
  error = false;

  private sub$ = new Subscription();

  constructor(
    private clockHourService: ClockHourService,
    private i18n: TranslateService,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (this.id) {
      this.loadChanges();
    }
  }

  ngOnDestroy(): void {
    this.sub$.unsubscribe();
  }

  toggleDropdown(): void {
    this.isOpen = !this.isOpen;
    this.cdRef.detectChanges();
  }

  private loadChanges(): void {
    this.isLoading = true;
    this.error = false;
    this.cdRef.detectChanges();

    this.sub$.add(
      this.clockHourService.getChangeHistory(this.id).subscribe(
        (response: ShiftHistory.Change[]) => {
          this.changes = this.formatChanges(response);
          this.isLoading = false;
          this.cdRef.detectChanges();
        },
        () => {
          this.changes = [];
          this.isLoading = false;
          this.cdRef.detectChanges();
        }
      )
    );
  }

  private formatChanges(
    changes: ShiftHistory.Change[]
  ): ShiftHistory.FormattedChange[] {
    return changes.map((change) => ({
      id: change.id,
      title: this.getChangeTitle(change.changeType),
      date: this.formatDate(change.occurredAt),
      time: this.formatTime(change.occurredAt),
      description: this.getChangeDescription(change),
      type: change.changeType,
    }));
  }

  getTextKey(key: string, isDescription: boolean): string {
    const path = isDescription ? 'history.changeDescriptions' : 'history';
    const titleKeys: Record<ShiftHistory.ChangeType, string> = {
      shift_added: this.i18n.instant(`clockHour.${path}.shiftAdded`),
      shift_removed: this.i18n.instant(`clockHour.${path}.shiftRemoved`),
      shift_interval_modified: this.i18n.instant(
        `clockHour.${path}.shiftIntervalModified`
      ),
      shift_ended: this.i18n.instant(`clockHour.${path}.shiftEnded`),
      break_added: this.i18n.instant(`clockHour.${path}.breakAdded`),
      break_removed: this.i18n.instant(`clockHour.${path}.breakRemoved`),
      break_interval_modified: this.i18n.instant(
        `clockHour.${path}.breakIntervalModified`
      ),
      break_reason_modified: this.i18n.instant(
        `clockHour.${path}.breakReasonModified`
      ),
      notes_modified: this.i18n.instant(`clockHour.${path}.notesModified`),
      type_modified: this.i18n.instant(`clockHour.${path}.typeModified`),
      approved: this.i18n.instant(`clockHour.${path}.approved`),
      rejected: this.i18n.instant(`clockHour.${path}.rejected`),
    };
    return titleKeys[key as ShiftHistory.ChangeType] || key;
  }

  private getChangeTitle(type: ShiftHistory.ChangeType): string {
    return this.getTextKey(type, false);
  }

  private getChangeDescription(change: ShiftHistory.Change): string {
    return this.createChangeDescriptionText(
      change.occurredBy,
      change.changeType,
      change.payload
    );
  }

  getChangesText(
    payload: ShiftHistory.ChangePayload,
    changeType: ShiftHistory.ChangeType
  ): string {
    const { oldValue, newValue } = payload;
    const hasOldValue = oldValue !== null && oldValue !== undefined;
    const hasNewValue = newValue !== null && newValue !== undefined;

    if (!hasOldValue && !hasNewValue) return '';

    const newValueStr = this.formatValue(newValue, changeType);
    const oldValueStr = this.formatValue(oldValue, changeType);

    if (hasOldValue && hasNewValue) {
      const fromPrefix = this.i18n.instant(
        'clockHour.history.changeDescriptions.from'
      );
      const toPrefix = this.i18n.instant(
        'clockHour.history.changeDescriptions.to'
      );
      const oldValueStr = this.formatValue(oldValue, changeType);

      return `${fromPrefix} ${oldValueStr} ${toPrefix} ${newValueStr}`;
    }

    return newValueStr || oldValueStr;
  }

  private createChangeDescriptionText(
    occurredBy: string,
    changeType: ShiftHistory.ChangeType,
    payload: ShiftHistory.ChangePayload
  ): string {
    const conditional = this.getChangesText(payload, changeType);

    const parts = [occurredBy, this.getTextKey(changeType, true)];
    if (conditional) {
      parts.push(conditional);
    }

    return parts.join(' ');
  }

  private formatValue(
    value: ShiftHistory.ChangePayloadValue,
    changeType: ShiftHistory.ChangeType
  ): string {
    if (!value) return '';
    if (typeof value === 'object' && value.start && value.end) {
      return `${value.start} - ${value.end}`;
    }

    return (changeType === 'type_modified'
      ? this.i18n.instant(`clockHour.type.${value}`)
      : value) as string;
  }

  private formatDate(dateString: string): string {
    moment.locale(this.i18n.currentLang);
    return moment(dateString).format('DD/MM/YY');
  }

  private formatTime(dateString: string): string {
    return moment(dateString).format('HH:mm');
  }
}
